/**
 * B3: Trigger Node Component — Sprint 17 enhanced with Lucide icons + CSS vars
 * Renders a trigger node in the journey canvas with icon and kind label.
 */
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import {
  ClipboardList, Tag, BarChart3, Zap, Users, Calendar, Hand,
} from "lucide-react";
import type { TriggerNodeData, TriggerKind } from "./types";

const TRIGGER_ICONS: Record<TriggerKind, LucideIcon> = {
  form_submitted: ClipboardList,
  tag_added: Tag,
  deal_stage_changed: BarChart3,
  event_fired: Zap,
  segment_entered: Users,
  date_reached: Calendar,
  manual_entry: Hand,
};

interface TriggerNodeProps {
  node: TriggerNodeData;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

export function TriggerNode({ node, isSelected, onSelect }: TriggerNodeProps) {
  const { t } = useTranslation();
  const Icon = TRIGGER_ICONS[node.triggerKind];

  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      className={cn(
        "flex items-center gap-2 rounded-lg border-2 px-4 py-3 text-left shadow-sm transition-colors",
        "bg-[var(--color-success)]/5 border-[var(--color-success)]/20 hover:border-[var(--color-success)]/50",
        isSelected && "ring-2 ring-[var(--color-success)] border-[var(--color-success)]/80",
      )}
    >
      <Icon className="h-5 w-5 text-[var(--color-success)]" />
      <div>
        <div className="text-xs font-medium text-[var(--color-success)] uppercase">
          {t("journey.node.trigger")}
        </div>
        <div className="text-sm font-medium text-[var(--color-foreground)]">
          {t(`journey.trigger.${node.triggerKind}`)}
        </div>
      </div>
    </button>
  );
}
